import type { ISODate } from "./types";

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface DateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}
export function parseISODate(value: string): DateParts {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) {
    throw new Error(`Expected an ISO calendar date (YYYY-MM-DD), received: ${value}`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    throw new Error(`Invalid ISO calendar date: ${value}`);
  }

  return { year, month, day };
}

export function toISODate(year: number, month: number, day: number): ISODate {
  const value = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  parseISODate(value);
  return value as ISODate;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function compareISODate(left: string, right: string): number {
  parseISODate(left);
  parseISODate(right);
  return left.localeCompare(right);
}

export function addCalendarDays(value: ISODate, days: number): ISODate {
  const { year, month, day } = parseISODate(value);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return toISODate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

export function isWeekend(value: ISODate): boolean {
  const { year, month, day } = parseISODate(value);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 || weekday === 6;
}
